import React, { useEffect, useRef } from "react";
import type { AnalysisResult } from "../api/mockData";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  history: AnalysisResult[];
  onNewAnalysis: () => void;
  onOpenResult: (id: string) => void;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function Sidebar({
  isOpen,
  onClose,
  history,
  onNewAnalysis,
  onOpenResult,
}: SidebarProps) {
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNewAnalysis = () => {
    onClose();
    onNewAnalysis();
  };

  const handleOpenItem = (id: string) => {
    onClose();
    onOpenResult(id);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`sidebar-backdrop ${isOpen ? "sidebar-backdrop--visible" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar panel */}
      <div
        ref={sidebarRef}
        className={`sidebar-panel ${isOpen ? "sidebar-panel--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Меню"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-[max(env(safe-area-inset-top),16px)] pb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-[10px] flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(145deg, var(--rose) 0%, var(--mauve) 100%)",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
                <path
                  d="M20 4C20 4 8 14 8 22C8 28.627 13.373 34 20 34C26.627 34 32 28.627 32 22C32 14 20 4 20 4Z"
                  fill="white"
                  fillOpacity="0.9"
                />
              </svg>
            </div>
            <span
              className="font-editorial text-[20px]"
              style={{ fontWeight: 500, color: "var(--foreground)" }}
            >
              Kibbe
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-200"
            style={{ background: "var(--muted)" }}
            aria-label="Закрыть меню"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M3 3L11 11M11 3L3 11"
                stroke="var(--muted-foreground)"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* New analysis button */}
        <div className="px-4 pb-3 pt-1">
          <button
            onClick={handleNewAnalysis}
            className="w-full h-[48px] rounded-[14px] flex items-center justify-center gap-2.5 font-body text-[15px] font-semibold transition-all duration-200 active:scale-[0.97]"
            style={{
              background:
                "linear-gradient(145deg, var(--rose) 0%, var(--mauve) 100%)",
              color: "white",
              boxShadow: "0 4px 16px oklch(0.48 0.14 12 / 0.2)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M9 3V15M3 9H15"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            Новый анализ
          </button>
        </div>

        {/* Divider */}
        <div className="mx-5 my-1" style={{ borderTop: "1px solid var(--border)" }} />

        {/* History */}
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6">
          {history.length > 0 ? (
            <>
              <div
                className="font-body text-[11px] uppercase tracking-[0.1em] px-1 mb-2.5 font-semibold"
                style={{ color: "var(--muted-foreground)" }}
              >
                История анализов
              </div>
              <div className="space-y-1.5">
                {history.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleOpenItem(item.id)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-[12px] text-left transition-all duration-200 active:scale-[0.98]"
                    style={{
                      background: "transparent",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "var(--muted)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {/* Thumbnail */}
                    <div className="w-[44px] h-[44px] rounded-[10px] overflow-hidden shrink-0">
                      {item.photoDataUrls[0] ? (
                        <img
                          src={item.photoDataUrls[0]}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center"
                          style={{ background: "var(--muted)" }}
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                          >
                            <path
                              d="M9 2C9 2 4 7 4 11C4 14.3 6.2 17 9 17C11.8 17 14 14.3 14 11C14 7 9 2 9 2Z"
                              fill="var(--muted-foreground)"
                              fillOpacity="0.3"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {item.personName && (
                        <div
                          className="font-body text-[14px] font-semibold truncate"
                          style={{ color: "var(--foreground)" }}
                        >
                          {item.personName}
                        </div>
                      )}
                      <div
                        className={`font-body truncate ${item.personName ? "text-[12px]" : "text-[14px] font-semibold"}`}
                        style={{ color: item.personName ? "var(--muted-foreground)" : "var(--foreground)" }}
                      >
                        {item.kibbeType.nameRu}
                      </div>
                      <div
                        className="font-body text-[11px] mt-0.5"
                        style={{ color: "var(--muted-foreground)", opacity: 0.7 }}
                      >
                        {formatDate(item.date)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: "var(--muted)" }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 8V12L15 15M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                    stroke="var(--muted-foreground)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span
                className="font-body text-[14px] font-medium text-center"
                style={{ color: "var(--muted-foreground)" }}
              >
                Пока нет анализов
              </span>
              <span
                className="font-body text-[12px] text-center mt-1"
                style={{ color: "var(--muted-foreground)", opacity: 0.7 }}
              >
                Загрузите фото, чтобы начать
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
